<script setup lang="ts">
import { computed } from 'vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import JournalAddModal from '../modals/JournalAddModal.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { useJournalsListPage } from '@/modules/journals'

const {
  activeFilterChips,
  applyFilter,
  currentPage,
  createdAtFrom,
  createdAtTo,
  datePresets,
  displayedItems,
  error,
  filterMenus,
  formatFullName,
  formatPersonName,
  getFilterIcon,
  handleFilterClick,
  hasActiveFilters,
  headers,
  isAddModalOpen,
  isCreatedAtActive,
  isCreatedAtMenuOpen,
  isFetching,
  isTreeFilterActive,
  isTreeFilterLoading,
  itemsPerPage,
  onJournalCreated,
  onRowClick,
  onTableOptionsUpdate,
  applyCreatedAtFilter,
  openCreatedAtFilter,
  removeFilter,
  resetFilters,
  searchQuery,
  setCreatedAtPreset,
  setSearchQuery,
  tableItemsPerPageOptions,
  tempSelectedFilters,
  totalItems,
  treeFilterStore,
} = useJournalsListPage()

type TreeFilterKey = 'departments' | 'responsible_employees' | 'created_by'

const treeFilterConfigs = computed<
  Array<{
    key: TreeFilterKey
    placeholder: string
    title: string
  }>
>(() => [
  {
    key: 'departments',
    placeholder: 'Поиск: Подразделения',
    title: 'Подразделения',
  },
  {
    key: 'responsible_employees',
    placeholder: 'Поиск: Ответственные',
    title: 'Ответственные',
  },
  {
    key: 'created_by',
    placeholder: 'Поиск: Создатель',
    title: 'Создатель',
  },
])
</script>

<template>
  <section>
    <div class="d-flex align-center justify-end gap-4 mb-5">
      <VTextField
        :model-value="searchQuery"
        density="compact"
        placeholder="Поиск..."
        clearable
        prepend-inner-icon="ri-search-line"
        class="journal-list-search"
        @update:model-value="
          (v: string | null) => setSearchQuery(v?.trim() || '')
        "
      />
      <VBtn
        v-if="hasActiveFilters"
        prepend-icon="ri-delete-bin-line"
        variant="outlined"
        color="secondary"
        size="small"
        @click="resetFilters"
      >
        Сбросить фильтры
      </VBtn>
    </div>

    <div
      v-if="activeFilterChips.length > 0"
      class="filter-chips-container mb-4"
    >
      <VChip
        v-for="chip in activeFilterChips"
        :key="`${chip.type}-${chip.value ?? 'n'}`"
        closable
        size="small"
        class="journal-filter-chip"
        @click:close="removeFilter(chip)"
      >
        {{ chip.label }}
      </VChip>
    </div>

    <VCard id="journal-list">
      <div class="wrapper">
        <VDataTableServer
          v-model:page="currentPage"
          v-model:items-per-page="itemsPerPage"
          :loading="isFetching"
          :headers="headers"
          :items="displayedItems"
          :items-length="totalItems"
          item-value="id"
          class="text-no-wrap custom-data-table"
          @update:options="onTableOptionsUpdate"
          @click:row="onRowClick"
        >
          <template #no-data>
            <div class="pa-6 text-medium-emphasis">
              <div v-if="error">
                Ошибка загрузки
              </div>
              <div v-else>
                Нет данных
              </div>
            </div>
          </template>

          <template
            v-for="cfg in treeFilterConfigs"
            :key="`header-${cfg.key}`"
            #[`header.${cfg.key}`]="{ column }"
          >
            <span class="th-content d-flex align-center">
              <span>{{ column?.title || cfg.title }}</span>

              <div
                v-if="isTreeFilterLoading(cfg.key)"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>

              <VMenu
                v-else
                v-model="filterMenus[cfg.key]"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="getFilterIcon(isTreeFilterActive(cfg.key))"
                    @click.stop="handleFilterClick(cfg.key)"
                  />
                </template>

                <UniversalFilterTree
                  :field-key="cfg.key"
                  module="journal"
                  :store="treeFilterStore"
                  :model-value="tempSelectedFilters[cfg.key] || []"
                  :placeholder="cfg.placeholder"
                  @update:model-value="tempSelectedFilters[cfg.key] = $event"
                  @apply="applyFilter(cfg.key)"
                />
              </VMenu>
            </span>
          </template>

          <template #header.created_at="{ column }">
            <span class="th-content d-flex align-center">
              <span>{{ column.title }}</span>
              <VMenu
                v-model="isCreatedAtMenuOpen"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
                :max-width="220"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="getFilterIcon(isCreatedAtActive)"
                    @click.stop="openCreatedAtFilter"
                  />
                </template>
                <VCard class="pa-3">
                  <VTextField
                    v-model="createdAtFrom"
                    type="date"
                    variant="outlined"
                    density="compact"
                    hide-details
                    placeholder="С"
                    class="mb-2"
                  />
                  <VTextField
                    v-model="createdAtTo"
                    type="date"
                    variant="outlined"
                    density="compact"
                    hide-details
                    placeholder="По"
                    class="mb-2"
                  />
                  <div class="d-flex flex-wrap gap-2 mb-2">
                    <VChip
                      v-for="preset in datePresets"
                      :key="preset.id"
                      size="small"
                      class="preset-chip"
                      @click="setCreatedAtPreset(preset.id)"
                    >
                      {{ preset.label }}
                    </VChip>
                  </div>
                  <VBtn
                    size="small"
                    color="primary"
                    block
                    @click="applyCreatedAtFilter"
                  >
                    Применить
                  </VBtn>
                </VCard>
              </VMenu>
            </span>
          </template>

          <template #item.name="{ item }">
            <div class="journal-name">
              {{ item.name }}
            </div>
          </template>

          <template #item.departments="{ item }">
            <div class="journal-departments">
              <template v-if="item.departments && item.departments.length">
                <div
                  v-for="dept in item.departments"
                  :key="dept.id"
                  class="journal-department-item"
                >
                  {{ dept.short_name || dept.name }}
                </div>
              </template>
              <template v-else>
                —
              </template>
            </div>
          </template>

          <template #item.responsible_employees="{ item }">
            <div class="journal-responsibles">
              <template
                v-if="
                  item.responsible_employees
                    && item.responsible_employees.length
                "
              >
                <div
                  v-for="emp in item.responsible_employees"
                  :key="emp.id"
                  class="journal-responsible-item"
                >
                  {{ formatPersonName(emp) }}
                </div>
              </template>
              <template v-else>
                —
              </template>
            </div>
          </template>

          <template #item.created_by="{ item }">
            <div class="journal-creator">
              {{ formatFullName(item.created_by) }}
            </div>
          </template>

          <template #item.created_at="{ item }">
            <div class="journal-created-at">
              {{
                item.created_at
                  ? new Date(item.created_at).toLocaleDateString("ru-RU")
                  : "—"
              }}
            </div>
          </template>

          <template #bottom>
            <div class="custom-data-table-footer">
              <VDataTableFooter
                show-current-page
                :items-per-page-options="tableItemsPerPageOptions"
              />
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>

    <JournalAddModal
      v-model:is-open="isAddModalOpen"
      @created="onJournalCreated"
    />
  </section>
</template>

<style scoped lang="scss">
@use '@/modules/journals/styles/dataTable' as dt;

@include dt.custom-data-table;
@include dt.custom-data-table-footer;

section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-block-size: 0;
}

.journal-list-search {
  inline-size: 16rem;
}

.journal-name {
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 8px;
}

:deep(.v-data-table__tr) {
  cursor: pointer;
  vertical-align: top;
}

:deep(.v-data-table__tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

:deep(.v-data-table__td) {
  vertical-align: top;
  padding: 1rem;
  height: auto;
}

:deep(.v-data-table__tr) {
  height: auto;
}

:deep(.v-data-table__td:nth-child(3)) {
  text-align: center;
  vertical-align: middle;
}

.wrapper {
  box-shadow: 0 4px 10px rgba(46, 38, 61, 0.2);
  background: rgb(var(--v-theme-paper));
  width: 100%;
  flex: 1;
  min-block-size: 0;
}

/* Делаем VCard flex-контейнером, чтобы flex-цепочка из _table.scss
   (.wrapper:has(.custom-data-table) > div → .v-table__wrapper) дотянулась
   до скролл-зоны и активировала sticky-шапку и фиксированный футер. */
:deep(#journal-list) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-block-size: 0;
}

:deep(.v-data-table) {
  width: 100%;
}

:deep(.v-data-table__wrapper) {
  width: 100%;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(
    var(--v-theme-primary-100),
    var(--v-overlay-scrim-opacity)
  ) !important;
  position: relative;
  padding: 1rem;
  vertical-align: middle;
}

:deep(.custom-data-table .v-data-table__td) {
  padding: 1rem;
}

.journal-creator,
.journal-created-at {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 8px;
}

.journal-departments {
  padding-right: 8px;
  text-align: left;
}

.journal-department-item {
  margin-bottom: 4px;
  line-height: 1.4;
  text-align: left;
}

.journal-department-item:last-child {
  margin-bottom: 0;
}

.journal-responsibles {
  padding-right: 8px;
  text-align: left;
}

.journal-responsible-item {
  margin-bottom: 4px;
  line-height: 1.4;
  text-align: left;
}

.journal-responsible-item:last-child {
  margin-bottom: 0;
}

.filter-chips-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  overflow-x: auto;
}

.journal-filter-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-icon-btn {
  margin-left: 4px;
}
</style>
